import {Component, Inject, Input, OnChanges, OnInit, Optional, SimpleChanges} from '@angular/core';
import {SurveyService} from '../../../services/surveys.service';
import {TranslateService} from '@ngx-translate/core';
import {ScriptLoaderService} from 'angular-google-charts';
import {ToastService} from '../../../services/general/toast.service';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {MatTableDataSource} from '@angular/material/table';
import {ModalShowEvidencesComponent} from '../../../modals/modal-show-evidences/modal-show-evidences.component';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {EChartsOption} from 'echarts';
import {ResponseTypesService} from '../../../services/config/response-types.service';

@Component({
  selector: 'app-single-question-responses',
  templateUrl: './single-question-responses.component.html',
  styleUrls: ['./single-question-responses.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({height: '0px', minHeight: '0'})),
      state('expanded', style({height: '*', minHeight: '*'})),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class SingleQuestionResponsesComponent implements OnInit {
  @Input() question;
  @Input() questionID;
  @Input() showPrivateInfo;
  @Input() viewOnlyChart = false;
  @Input() isDialog = false;

  loading = false;
  heatmapOptions: EChartsOption | null = null;
  selectedTabIndex = 0;

  responses = new MatTableDataSource([]);

  constructor(
    public surveyService: SurveyService,
    public translateService: TranslateService,
    private loaderService: ScriptLoaderService,
    private dialog: MatDialog,
    public responsesTypeService: ResponseTypesService,
    @Optional() @Inject(MatDialogRef) public dialogRef: MatDialogRef<SingleQuestionResponsesComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any
  ) {
  }

  chartLoaded = false;
  questionContentLoaded = false;
  analyticsFields = [
    {parameter: 'mean', translateString: 'generic.average'},
    {parameter: 'geometric_mean', translateString: 'generic.geometric-average'},
    {parameter: 'mode', translateString: 'generic.mode'},
    {parameter: 'median', translateString: 'generic.median'},
    {parameter: 'variance', translateString: 'generic.variance'},
    {parameter: 'q1', translateString: 'Q1'},
    {parameter: 'q2', translateString: 'Q2'},
    {parameter: 'q3', translateString: 'Q3'},
    {parameter: 'q4', translateString: 'Q4'},
  ];

  ngOnInit(): void {
    if (this.data) {
      if (this.data.questionID !== undefined) {
        this.questionID = this.data.questionID;
      }
      if (this.data.showPrivateInfo !== undefined) {
        this.showPrivateInfo = this.data.showPrivateInfo;
      }
      if (this.data.isDialog !== undefined) {
        this.isDialog = this.data.isDialog;
      }

    }
    const callback = () => {
      this.init();
      const interval = setInterval(() => {
        if (!this.chartLoaded) {
          const prefix = 'chart_question_id_' + this.question.id;
          const matches = document.querySelectorAll(`[id^="${prefix}"]`);
          const chartDiv = matches.length > 0 ? matches[0] : null;
          if (chartDiv) {
            this.loadChartPackages();
            this.chartLoaded = true;
            clearInterval(interval);
          }
        }
      }, 200);
    };

    if (this.questionID) {
      this.loading = true;
      this.surveyService.getQuestionWithResponses(this.questionID, (question) => {
        this.question = question;
        this.loading = false;
        callback();
      }, (err) => {
        this.loading = false;
      });
    } else {
      callback();
    }
  }

  init(): void {
    if (this.surveyService.isOpenedQuestion(this.question.typology)) {
      this.question.content = '';
      this.question?.responses?.forEach((response) => {
        this.question.content += response.answer + '. ';
      });
      this.questionContentLoaded = true;
    }
    this.responses = new MatTableDataSource(this.question.responses || []);
  }

  loadChartPackages(): void {
    this.loaderService.loadChartPackages('corechart').subscribe(() => {
      google.charts.load('current', {packages: ['corechart', 'controls', 'bar']});
      google.charts.setOnLoadCallback(this.drawCharts);
    });
  }

  getRandomColor(): string {
    const letters = '0123456789ABCDEF'.split('');
    let color = '#';
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  }

  drawCharts = () => {
    if (this.surveyService.isMultipleChoiceQuestion(this.question.typology)) {
      const tmp = [
        [this.translateService.instant('generic.option'), this.translateService.instant('components.responses.responses')],
      ];
      this.question?.choices?.forEach(choice => {
        tmp.push([choice.title, choice.answers_count]);
      });

      const abstainedAnswers = this.responses?.data?.filter((response) => response.status === 'ABSTAINED');

      if (abstainedAnswers.length > 0) {
        tmp.push([this.translateService.instant('generic.users-abstained'), abstainedAnswers.length]);
      }

      const data = google.visualization.arrayToDataTable(tmp);
      const options = {
        sliceVisibilityThreshold: 0,
      };

      const chart = new google.visualization.PieChart(document.getElementById('chart_question_id_' + this.question.id));
      chart.draw(data, options);
    }
    if (this.surveyService.isCheckboxQuestion(this.question.typology)) {
      const tmp = [
        [this.translateService.instant('generic.option'), this.translateService.instant('components.responses.responses'), {role: 'style'}],
      ];
      this.question?.choices?.forEach(choice => {
        tmp.push([choice.title, choice.answers_count, choice.color]);
      });

      const abstainedAnswers = this.responses?.data?.filter((response) => response.status === 'ABSTAINED');

      if (abstainedAnswers.length > 0) {
        tmp.push([this.translateService.instant('generic.users-abstained'), abstainedAnswers.length, this.getRandomColor()]);
      }

      const data = google.visualization.arrayToDataTable(tmp);
      const maxValue = Math.max(...tmp.slice(1).map(row => row[1]));
      const options = {
        legend: 'none',
        vAxis: {
          minValue: 0,
          maxValue: maxValue + 1,
          format: '0',
        },
        hAxis: {
          format: '0',
          gridlines: {count: Math.ceil(maxValue) + 2}
        }
      };

      const chart = new google.visualization.BarChart(document.getElementById('chart_question_id_' + this.question.id));
      // @ts-ignore
      chart.draw(data, options);
    }
    if (this.surveyService.isQuantitativeQuestion(this.question.typology)) {
      const tmp = [
        [this.translateService.instant('generic.value'), this.translateService.instant('components.responses.responses')],
      ];

      const max_range = parseFloat(this.question?.max);
      const min_range = parseFloat(this.question?.min);
      const step = parseFloat(this.question?.step) || 1;

      const answerCount = this.question?.incidence;

      for (let i = min_range; i <= max_range; i += step) {
        const key = i.toFixed(2);
        if (!(key in answerCount)) {
          answerCount[key] = 0;
        }
      }

      Object.keys(answerCount).sort((a, b) => parseFloat(a) - parseFloat(b)).forEach(key => {
        tmp.push([parseFloat(key), answerCount[key]]);
      });

      const data = google.visualization.arrayToDataTable(tmp);
      const options = {
        legend: 'none',
        vAxis: {
          format: '#',
        }
      };
      const chart = new google.visualization.ColumnChart(document.getElementById('chart_question_id_' + this.question.id));
      // @ts-ignore
      chart.draw(data, options);
      const boxPlotData = google.visualization.arrayToDataTable([
        ['Label', 'Min', 'Q1', 'Median', 'Q3', 'Max'],
        [
          'Data',
          this.question?.quantitative_results?.min,
          this.question?.quantitative_results?.q1,
          this.question?.quantitative_results?.median,
          this.question?.quantitative_results?.q3,
          this.question?.quantitative_results?.max
        ]
      ]);

      const boxPlotOptions = {
        title: 'Box Plot',
        legend: 'none',
        seriesType: 'candlesticks',
        series: {1: {type: 'line'}},
        orientation: 'horizontal',
        boxStyle: {stroke: '#888', strokeWidth: 1, fill: '#888', fillOpacity: 0.3}
      };

      const boxPlotChart =
        new google.visualization.CandlestickChart(document.getElementById('chart_box_plot_question_id_' + this.question.id));
      // @ts-ignore
      boxPlotChart.draw(boxPlotData, boxPlotOptions);
    }
    if (this.surveyService.isConsensusQuestion(this.question.typology)) {
      const tmp = [
        [this.translateService.instant('generic.option'), this.translateService.instant('components.responses.responses')],
      ];

      tmp.push([this.translateService.instant('generic.agree'), this.question.agreeCount]);
      tmp.push([this.translateService.instant('generic.disagree'), this.question.disagreeCount]);
      tmp.push([this.translateService.instant('generic.abstained'), this.question.abstainedCount]);

      const data = google.visualization.arrayToDataTable(tmp);
      const options = {
        height: 200,
        pieHole: 0.4,
        enableInteractivity: false,
        pieSliceText: 'none',
        slices: {
          0: {color: '#00bc1d'},
          1: {color: '#ff0000'},
          2: {color: '#e5e3e3'},
        },
        animation: {
          startup: true,
        },
      };

      const chart = new google.visualization.PieChart(document.getElementById('chart_question_id_' + this.question.id));
      chart.draw(data, options);

      const data2 = google.visualization.arrayToDataTable([
        ['Element', 'Density', {role: 'style'}],
        [
          this.translateService.instant('generic.certainty-degree'),
          {
            v: this.question?.results?.certainty_degree,
            f: this.question?.results?.certainty_degree + '%',
          },
          '#1b61a1',
        ],
        [
          this.translateService.instant('generic.contradiction-degree'),
          {
            v: this.question?.results?.contradiction_degree,
            f: this.question?.results?.contradiction_degree + '%',
          },
          '#ff0000',
        ],
      ]);

      const view = new google.visualization.DataView(data2);
      view.setColumns([
        0,
        1,
        {
          calc: 'stringify',
          sourceColumn: 1,
          type: 'string',
          role: 'annotation',
        },
        2,
      ]);

      const options2 = {
        height: 200,
        pieHole: 0.4,
        enableInteractivity: false,
        pieSliceText: 'none',
        slices: {
          0: {color: '#00bc1d'},
          1: {color: '#ff0000'},
          2: {color: '#e5e3e3'},
        },
        animation: {
          startup: true,
        },
      };

      const chart2 = new google.visualization.BarChart(
        document.getElementById('chart_parameters_div_' + this.question.id)
      );

      chart2.draw(view, options2);
    }
    if (this.surveyService.isGridQuestion(this.question.typology)) {
      this.generateHeatmapOptions();
      this.drawGridCharts();
    }
  };

  drawGridCharts(): void {
    const rows = this.question.grid_rows;
    const cols = this.question.grid_columns;

    rows.forEach(row => {
      const dataArray = [['Option', 'Responses']];

      Object.keys(row?.answers_count)?.forEach((colId) => {
        dataArray.push([cols?.find((col) => col.id == colId)?.title, row?.answers_count[colId] || 0]);
      });


      const data = google.visualization.arrayToDataTable(dataArray);
      const options = {
        pieHole: 0.4,
        height: 180,
        sliceVisibilityThreshold: 0,
      };

      const chartEl = document.getElementById(`chart_question_id_${this.question.id}_${row.id}`);
      if (chartEl) {
        const chart = new google.visualization.PieChart(chartEl);
        chart.draw(data, options);
      }
    });
  }


  private generateHeatmapOptions(): void {
    const rows = this.question.grid_rows.reverse();
    const cols = this.question.grid_columns;

    const heatmapData: (number | string)[][] = [];
    const maxValue = 0;
    cols?.forEach((col, colIndex) => {
      rows.forEach((row, rowIndex) => {
        heatmapData.push([colIndex, rowIndex, row?.answers_count[col?.id] || 0]);
      });
    });

    // Calcolo dinamico dei margini
    const maxRowLabelLength = Math.max(...rows.map(r => r.title.length));
    const maxColLabelLength = Math.max(...cols.map(c => c.title.length));

    const leftMargin = Math.min(200, 12 + maxRowLabelLength * 7);   // max 300px
    const bottomMargin = Math.min(200, 20 + maxColLabelLength * 6); // max 200px

    this.heatmapOptions = {
      tooltip: {
        position: 'top'
      },
      grid: {
        top: '10%',
        left: leftMargin,
        bottom: bottomMargin,
        right: 20
      },
      xAxis: {
        type: 'category',
        data: cols.map(c => c.title.replace(/(.{15})/g, '$1\n')), // wrapping soft
        axisLabel: {
          fontSize: 12,
          lineHeight: 16,
          interval: 0
        },
        splitArea: {
          show: true
        }
      },
      yAxis: {
        type: 'category',
        data: rows.map(r => r.title.replace(/(.{25})/g, '$1\n')),
        axisLabel: {
          fontSize: 12,
          lineHeight: 16
        },
        splitArea: {
          show: true
        }
      },
      visualMap: {
        min: 0,
        max: maxValue,
        calculable: true,
        orient: 'horizontal',
        left: 'center',
        bottom: '5%'
      },
      series: [
        {
          name: this.translateService.instant('components.responses.responses'),
          type: 'heatmap',
          data: heatmapData,
          label: {
            show: true
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }
      ]
    };
    this.selectedTabIndex = 0;
  }

  showExpertEvideces(element): void {
    const dialogRef = this.dialog.open(ModalShowEvidencesComponent, {
      panelClass: 'medium-dialog',
      data: {
        userFullName: element?.name,
        attachments: element?.attachments,
      },
    });

    dialogRef.afterClosed().subscribe((event) => {
    });
  }

  get columnsForTypology(): string[] {
    const typology = this.question?.typology;
    if (this.surveyService.isConsensusQuestion(typology)) {
      return ['name', 'sentiment', 'feedback', 'answer', 'confidence', 'evidences'];
    }

    if (this.surveyService.isOpenedQuestion(typology)) {
      return ['name', 'sentiment', 'answer', 'feedback', 'evidences'];
    }

    if (this.surveyService.isMultipleChoiceQuestion(typology) ||
      this.surveyService.isCheckboxQuestion(typology) ||
      this.surveyService.isQuantitativeQuestion(typology)) {
      return ['name', 'answer', 'feedback', 'evidences'];
    }

    return ['name', 'answer', 'feedback', 'evidences'];
  }

  closeDialogue(event: any = null) {
    this.dialogRef.close(event);
  }
}
